<script setup lang="ts">
import { onMounted } from 'vue'

import { Button, ButtonGroup, TextInput } from '@collector/shared-ui'
import { ParticipantLogo } from '@collector/shared-ui-domain'
import { Participant } from '@collector/sportsapi-client-legacy'

import type {
  ManualIncidentEventStatusOption,
  ManualIncidentGroupData,
  ManualIncidentPayload,
} from './manual-incident.types'
import ManualIncidentGroup from './ManualIncidentGroup.vue'

defineProps<{
  homeParticipant?: Participant
  awayParticipant?: Participant
  isSwitchedTeamColors?: boolean
  incidentGroups: ManualIncidentGroupData[]
  eventStatuses: ManualIncidentEventStatusOption[]
  eventStatusLayout?: { containerClass?: string }
  showClock?: boolean
  minutesMaxDigits?: number
}>()

const payload = defineModel<ManualIncidentPayload>({ required: true })

onMounted(() => {
  Object.assign(payload.value, {
    minute: '',
    second: '',
    participant_team_id: null,
    incident_id: null,
    status_id: null,
  })
})

const payloadButtonClass = 'justify-start px-3! text-wrap! font-bold text-xs'
</script>

<template>
  <div class="space-y-3">
    <ButtonGroup
      :options="[
        ...(homeParticipant ? [{ value: homeParticipant }] : []),
        ...(awayParticipant ? [{ value: awayParticipant }] : []),
      ]"
      width="full"
      withSeparator
    >
      <template #default="{ option, selected }">
        <Button
          class="justify-start!"
          :class="{
            'font-bold': selected,
            'bg-neutral-light-10 text-neutral-dark-2': !selected,
            'bg-current-team-home-5':
              option.value === homeParticipant && selected,
            'text-neutral-light-10':
              (option.value === homeParticipant &&
                isSwitchedTeamColors &&
                selected) ||
              (option.value === awayParticipant &&
                !isSwitchedTeamColors &&
                selected),
            'bg-current-team-away-5':
              option.value === awayParticipant && selected,
            'rounded-r-none': option.value === homeParticipant,
            'flex-row-reverse rounded-l-none': option.value === awayParticipant,
          }"
          size="md"
          @click="payload.participant_team_id = option.value.id"
        >
          <ParticipantLogo :participant="option.value" />
          {{ option.value.short_name }}
        </Button>
      </template>
    </ButtonGroup>

    <div class="modal-content">
      <div class="w-full text-center font-bold">Incidents</div>
      <template v-for="incidentGroup in incidentGroups">
        <ManualIncidentGroup
          v-if="incidentGroup.incidents"
          :key="incidentGroup.title"
          :grid="!incidentGroup.layout"
          :gridClass="incidentGroup.layout?.containerClass ?? ''"
          titleClass="text-xs"
        >
          <template #title>{{ incidentGroup.title }}</template>

          <slot
            v-for="incidentId in incidentGroup.incidents"
            :key="incidentId"
            :class="[
              incidentGroup.layout && incidentGroup.layout.buttonClass,
              payloadButtonClass,
            ]"
            name="incidentButton"
            :incidentId
            :disabled="payload.participant_team_id === null"
            :selected="payload.incident_id === incidentId"
            :onClick="() => (payload.incident_id = incidentId)"
          />
        </ManualIncidentGroup>
      </template>

      <div class="mt-6">
        <ManualIncidentGroup
          :grid="!eventStatusLayout"
          :gridClass="eventStatusLayout?.containerClass ?? ''"
          titleClass="font-bold text-sm"
        >
          <template #title>Event statuses</template>

          <Button
            v-for="eventStatus in eventStatuses"
            :key="eventStatus.id"
            :class="payloadButtonClass"
            variant="highlighted"
            size="lg"
            :disabled="payload.participant_team_id === null"
            :selected="payload.status_id === eventStatus.id"
            @click="payload.status_id = eventStatus.id"
          >
            {{ eventStatus.name }}
          </Button>
        </ManualIncidentGroup>
      </div>

      <ManualIncidentGroup v-if="showClock">
        <template #title>Event time</template>
        <div class="flex items-center justify-center space-x-2">
          <TextInput
            v-model.number="payload.minute"
            numeric
            :maxlength="minutesMaxDigits"
            :disabled="payload.participant_team_id === null"
            size="md"
            placeholder="0"
            inputClass="w-12 text-center"
          />
          <span>:</span>
          <TextInput
            v-model.number="payload.second"
            numeric
            :disabled="payload.participant_team_id === null"
            :maxlength="2"
            size="md"
            placeholder="0"
            inputClass="w-12 text-center"
          />
        </div>
      </ManualIncidentGroup>
    </div>
  </div>
</template>

<style scoped>
.modal-content {
  @apply max-h-[calc(100vh-13rem)] overflow-y-auto;
}
</style>
